import React, { useEffect, useState, useCallback } from 'react';
import { listUsers, createUserAdmin, updateUserAdmin, deleteUserAdmin } from '../api/client';

const PERM_LABELS = {
  ols:       'OLS Surfaces',
  ols_adg:   'OLS (Amendment 18)',
  pa:        'Precision Approach',
  npa:       'Non-Precision Approach',
  vss:       'VSS',
  circling:  'Circling',
  sid_ois:   'SID / Departure OIS',
  star_ois:  'STAR / Arrival OIS',
  msa:       'MSA / TAA',
  rnav:      'RNAV Routes',
  airspace:  'Airspace',
  rmac:      'RMAC',
  cns:       'CNS',
};
const PERM_KEYS = Object.keys(PERM_LABELS);

const EMPTY_FORM = {
  username: '', password: '', role: 'general',
  full_name: '', email: '',
  permissions: Object.fromEntries(PERM_KEYS.map(k => [k, false])),
};

const s = {
  page:    { padding: '24px 28px', maxWidth: 1100, margin: '0 auto' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title:   { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  card:    { background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', borderRadius: 12, overflow: 'hidden' },
  th:      { padding: '10px 14px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary)', textAlign: 'left', borderBottom: '1px solid var(--glass-border)', whiteSpace: 'nowrap' },
  td:      { padding: '11px 14px', fontSize: 13, color: 'var(--text-secondary)', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  badge:   (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
    borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: active ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.12)',
    color: active ? '#34c759' : '#ff3b30',
    border: `1px solid ${active ? 'rgba(52,199,89,0.3)' : 'rgba(255,59,48,0.3)'}`,
  }),
  roleBadge: (role) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: role === 'admin' ? 'rgba(198,167,68,0.15)' : 'rgba(100,150,255,0.12)',
    color: role === 'admin' ? '#C6A744' : '#7ca8ff',
    border: `1px solid ${role === 'admin' ? 'rgba(198,167,68,0.35)' : 'rgba(100,150,255,0.25)'}`,
  }),
  btn: (variant = 'primary', small = false) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: small ? '5px 12px' : '8px 16px',
    fontSize: small ? 12 : 13, fontWeight: 600, cursor: 'pointer', borderRadius: 8,
    border: '1px solid',
    ...(variant === 'primary'  ? { background: '#C6A744', borderColor: '#C6A744', color: '#0f172a' } :
        variant === 'danger'   ? { background: 'rgba(255,59,48,0.12)', borderColor: 'rgba(255,59,48,0.4)', color: '#ff3b30' } :
        variant === 'ghost'    ? { background: 'transparent', borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' } :
                                 { background: 'var(--glass-fill)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }),
  }),
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#0f1e30', border: '1px solid var(--glass-border)', borderRadius: 16,
    padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
  },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 5 },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--glass-border)',
    borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)',
    outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', background: '#0d1a29', border: '1px solid var(--glass-border)',
    borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)',
    outline: 'none', boxSizing: 'border-box',
  },
  formRow: { marginBottom: 16 },
  permGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: 8 },
  permItem: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
    background: 'rgba(255,255,255,0.03)', borderRadius: 6, cursor: 'pointer',
    border: '1px solid transparent',
  },
  err: { background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.35)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#ff6b6b', marginBottom: 16 },
  noData: { textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)', fontSize: 13 },
};

function Modal({ title, onClose, children }) {
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PermissionToggles({ perms, onChange, disabled }) {
  return (
    <div style={s.permGrid}>
      {PERM_KEYS.map(k => (
        <label key={k} style={{ ...s.permItem, border: perms[k] ? '1px solid rgba(198,167,68,0.3)' : '1px solid transparent', opacity: disabled ? 0.5 : 1 }}>
          <input
            type="checkbox"
            checked={!!perms[k]}
            disabled={disabled}
            onChange={e => onChange({ ...perms, [k]: e.target.checked })}
            style={{ accentColor: '#C6A744', width: 14, height: 14, cursor: disabled ? 'default' : 'pointer' }}
          />
          <span style={{ fontSize: 12, color: perms[k] ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{PERM_LABELS[k]}</span>
        </label>
      ))}
    </div>
  );
}

function UserForm({ initial, onSubmit, onClose, loading, error, isCreate }) {
  const [form, setForm] = useState(initial);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <>
      {error && <div style={s.err}>{error}</div>}
      <div style={s.formRow}>
        <label style={s.label}>Username {isCreate && '*'}</label>
        <input style={s.input} value={form.username} onChange={e => set('username', e.target.value)} disabled={!isCreate} placeholder="username" />
      </div>
      <div style={s.formRow}>
        <label style={s.label}>{isCreate ? 'Password *' : 'New Password (leave blank to keep)'}</label>
        <input style={s.input} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={isCreate ? 'password' : '••••••'} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={s.label}>Full Name</label>
          <input style={s.input} value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Full Name" />
        </div>
        <div>
          <label style={s.label}>Email</label>
          <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
        </div>
      </div>
      <div style={s.formRow}>
        <label style={s.label}>Role</label>
        <select style={s.select} value={form.role} onChange={e => set('role', e.target.value)}>
          <option value="general">General</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      {!isCreate && (
        <div style={s.formRow}>
          <label style={s.label}>Status</label>
          <select style={s.select} value={form.is_active ? 'active' : 'inactive'} onChange={e => set('is_active', e.target.value === 'active')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      )}
      <div style={s.formRow}>
        <label style={{ ...s.label, marginBottom: 10 }}>Feature Permissions {form.role === 'admin' && <span style={{ color: '#C6A744', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(admin has all)</span>}</label>
        <PermissionToggles
          perms={form.permissions}
          onChange={perms => set('permissions', perms)}
          disabled={form.role === 'admin'}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={s.btn('ghost')} onClick={onClose}>Cancel</button>
        <button style={s.btn('primary')} onClick={() => onSubmit(form)} disabled={loading}>
          {loading ? 'Saving…' : (isCreate ? 'Create User' : 'Save Changes')}
        </button>
      </div>
    </>
  );
}

export default function UserManagement() {
  const [users, setUsers]       = useState([]);
  const [fetching, setFetching] = useState(true);
  const [fetchErr, setFetchErr] = useState('');
  const [modal, setModal]       = useState(null); // null | 'create' | { user }
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [formErr, setFormErr]   = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setFetching(true);
    setFetchErr('');
    listUsers()
      .then(r => setUsers(r.data))
      .catch(e => setFetchErr(e.response?.data?.detail || 'Failed to load users'))
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => { setModal(null); setFormErr(''); };

  const handleCreate = async (form) => {
    if (!form.username || !form.password) { setFormErr('Username and password are required'); return; }
    setSaving(true); setFormErr('');
    try {
      await createUserAdmin({ ...form, full_name: form.full_name || null, email: form.email || null });
      closeModal(); load();
    } catch (e) {
      setFormErr(e.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleEdit = async (form) => {
    setSaving(true); setFormErr('');
    const payload = {
      role: form.role,
      is_active: form.is_active,
      full_name: form.full_name || null,
      email: form.email || null,
      permissions: form.permissions,
    };
    if (form.password) payload.password = form.password;
    try {
      await updateUserAdmin(modal.user.id, payload);
      closeModal(); load();
    } catch (e) {
      setFormErr(e.response?.data?.detail || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteUserAdmin(delTarget.id);
      setDelTarget(null); load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  const permCount = (u) => {
    if (u.role === 'admin') return 'All';
    const count = PERM_KEYS.filter(k => u.permissions?.[k]).length;
    return `${count} / ${PERM_KEYS.length}`;
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>User Management</h1>
        <button style={s.btn('primary')} onClick={() => setModal('create')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add User
        </button>
      </div>

      {fetchErr && <div style={{ ...s.err, marginBottom: 20 }}>{fetchErr}</div>}

      <div style={s.card}>
        {fetching ? (
          <div style={s.noData}>Loading users…</div>
        ) : users.length === 0 ? (
          <div style={s.noData}>No users found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Username', 'Full Name', 'Role', 'Permissions', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ ...s.td, fontWeight: 600, color: 'var(--text-primary)' }}>{u.username}</td>
                  <td style={s.td}>{u.full_name || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                  <td style={s.td}><span style={s.roleBadge(u.role)}>{u.role}</span></td>
                  <td style={s.td}>{permCount(u)}</td>
                  <td style={s.td}><span style={s.badge(u.is_active)}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td style={s.td}>
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
                      : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        style={s.btn('default', true)}
                        onClick={() => setModal({
                          user: u,
                          form: {
                            username: u.username, password: '',
                            role: u.role, full_name: u.full_name || '', email: u.email || '',
                            is_active: u.is_active,
                            permissions: { ...Object.fromEntries(PERM_KEYS.map(k => [k, false])), ...u.permissions },
                          }
                        })}
                      >Edit</button>
                      <button style={s.btn('danger', true)} onClick={() => setDelTarget(u)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal === 'create' && (
        <Modal title="Create User" onClose={closeModal}>
          <UserForm
            initial={EMPTY_FORM}
            onSubmit={handleCreate}
            onClose={closeModal}
            loading={saving}
            error={formErr}
            isCreate
          />
        </Modal>
      )}

      {modal && modal.user && (
        <Modal title={`Edit — ${modal.user.username}`} onClose={closeModal}>
          <UserForm
            initial={modal.form}
            onSubmit={handleEdit}
            onClose={closeModal}
            loading={saving}
            error={formErr}
            isCreate={false}
          />
        </Modal>
      )}

      {delTarget && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, width: 400 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Delete User</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{delTarget.username}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={s.btn('ghost')} onClick={() => setDelTarget(null)}>Cancel</button>
              <button style={s.btn('danger')} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
